/**
 * Excel export utility for report pages.
 * Uses ExcelJS for styled, multi-sheet workbooks.
 */

import ExcelJS from "exceljs";

export type ExportMode = "global" | "by-district" | "by-region";

export interface ExportColumn<T> {
  label: string;
  getValue: (row: T) => string | number | null;
  /** Approximate character width hint (optional, auto-sized if omitted). */
  width?: number;
}

interface BuildOptions<T> {
  mode: ExportMode;
  rows: T[];
  columns: ExportColumn<T>[];
  /** Returns the primary group key (district name or region name) for a row. */
  getGroupName: (row: T) => string | null;
  /** "Distrito" or "Región" — used in summary sheet and null-group tab label. */
  groupLabel: string;
  /**
   * For `by-region` mode only: returns the parent district name for a row.
   * Drives tab ordering (by district first) and per-district tab colors.
   */
  getParentGroupName?: (row: T) => string | null;
}

// ─── Style constants ──────────────────────────────────────────────────────────

const HEADER_BG = "FF334155";  // slate-700
const HEADER_FG = "FFFFFFFF";  // white
const SUMMARY_BG = "FF1E3A5F"; // dark navy
const TOTAL_BG = "FFcbd5e1";   // slate-200

const TAB_COLORS: Record<ExportMode, string> = {
  global: "FF3B82F6",        // blue
  "by-district": "FF22C55E", // green
  "by-region": "FFF97316",   // orange (fallback; region tabs use per-district palette)
};

/**
 * 12-color palette for per-district region tab coloring.
 * Colors are distinct and legible as Excel tab labels.
 */
const DISTRICT_PALETTE = [
  "FF3B82F6", // blue-500
  "FF22C55E", // green-500
  "FFF97316", // orange-500
  "FFA855F7", // purple-500
  "FFEC4899", // pink-500
  "FF14B8A6", // teal-500
  "FFEAB308", // yellow-500
  "FFEF4444", // red-500
  "FF6366F1", // indigo-500
  "FF06B6D4", // cyan-500
  "FF84CC16", // lime-500
  "FFF43F5E", // rose-500
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function applyHeaderRow(
  sheet: ExcelJS.Worksheet,
  labels: string[],
  bgArgb = HEADER_BG
) {
  const row = sheet.addRow(labels);
  row.height = 20;
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: HEADER_FG }, size: 11 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgArgb } };
    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: false };
    cell.border = {
      bottom: { style: "thin", color: { argb: "FF94A3B8" } },
    };
  });
  sheet.views = [{ state: "frozen", xSplit: 0, ySplit: 1, activeCell: "A2" }];
}

function addDataRows<T>(
  sheet: ExcelJS.Worksheet,
  rows: T[],
  columns: ExportColumn<T>[]
) {
  rows.forEach((row) => {
    sheet.addRow(columns.map((c) => c.getValue(row) ?? ""));
  });
}

function autoSizeColumns<T>(
  sheet: ExcelJS.Worksheet,
  columns: ExportColumn<T>[],
  rows: T[]
) {
  sheet.columns.forEach((col, i) => {
    const def = columns[i];
    if (!def) return;
    const headerLen = def.label.length;
    const maxContent = rows.reduce((max, row) => {
      const val = String(def.getValue(row) ?? "");
      return Math.max(max, val.length);
    }, 0);
    col.width = Math.min(Math.max(headerLen + 2, maxContent + 2, def.width ?? 0), 55);
  });
}

/** Excel sheet names: max 31 chars, forbidden chars: \ / ? * [ ] : */
function sanitizeSheetName(name: string): string {
  return name.replace(/[\\/?*[\]:]/g, "").trim().substring(0, 31) || "Sin nombre";
}

// ─── Core builder ─────────────────────────────────────────────────────────────

export async function buildExcelWorkbook<T>(
  opts: BuildOptions<T>
): Promise<ExcelJS.Workbook> {
  const wb = new ExcelJS.Workbook();
  wb.creator = "ADB Admin Portal";
  wb.created = new Date();

  const labels = opts.columns.map((c) => c.label);

  // ── Global: single sheet ─────────────────────────────────────────────────────

  if (opts.mode === "global") {
    const sheet = wb.addWorksheet("Datos", {
      properties: { tabColor: { argb: TAB_COLORS.global } },
    });
    applyHeaderRow(sheet, labels);
    addDataRows(sheet, opts.rows, opts.columns);
    autoSizeColumns(sheet, opts.columns, opts.rows);
    return wb;
  }

  // ── By-district: one sheet per district ─────────────────────────────────────

  if (opts.mode === "by-district") {
    const nullKey = "Sin Distrito";
    const groups = new Map<string, T[]>();

    opts.rows.forEach((row) => {
      const key = opts.getGroupName(row) ?? nullKey;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(row);
    });

    const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
      if (a === nullKey) return 1;
      if (b === nullKey) return -1;
      return a.localeCompare(b, "es");
    });

    const tabColor = { argb: TAB_COLORS["by-district"] };

    // Summary sheet
    const summary = wb.addWorksheet("Resumen", { properties: { tabColor } });
    applyHeaderRow(summary, ["Distrito", "Registros"], SUMMARY_BG);
    sortedKeys.forEach((key) => {
      summary.addRow([key, groups.get(key)!.length]);
    });
    const totalRow = summary.addRow(["TOTAL", opts.rows.length]);
    totalRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_BG } };
    });
    summary.getColumn(1).width = 38;
    summary.getColumn(2).width = 12;

    // Data sheets
    sortedKeys.forEach((key) => {
      const groupRows = groups.get(key)!;
      const sheet = wb.addWorksheet(sanitizeSheetName(key), { properties: { tabColor } });
      applyHeaderRow(sheet, labels);
      addDataRows(sheet, groupRows, opts.columns);
      autoSizeColumns(sheet, opts.columns, groupRows);
    });

    return wb;
  }

  // ── By-region: tabs ordered by parent district, colored per district ─────────

  const nullRegion = "Sin Región";
  const nullDistrict = "Sin Distrito";

  // composite key "district||region" → { rows, district, regionName }
  const regionMap = new Map<string, { rows: T[]; district: string; regionName: string }>();

  opts.rows.forEach((row) => {
    const regionName = opts.getGroupName(row) ?? nullRegion;
    const district = opts.getParentGroupName?.(row) ?? nullDistrict;
    const compositeKey = `${district}||${regionName}`;
    if (!regionMap.has(compositeKey)) {
      regionMap.set(compositeKey, { rows: [], district, regionName });
    }
    regionMap.get(compositeKey)!.rows.push(row);
  });

  // Collect unique districts sorted alphabetically (nullDistrict last)
  const allDistricts = Array.from(
    new Set(Array.from(regionMap.values()).map((v) => v.district))
  ).sort((a, b) => {
    if (a === nullDistrict) return 1;
    if (b === nullDistrict) return -1;
    return a.localeCompare(b, "es");
  });

  // Assign one palette color per district (stable, index-based)
  const districtColorMap = new Map<string, string>();
  allDistricts.forEach((d, i) => {
    districtColorMap.set(d, DISTRICT_PALETTE[i % DISTRICT_PALETTE.length]);
  });

  // Sort regions: by district order first, then by region name; nullRegion last within district
  const sortedRegionKeys = Array.from(regionMap.keys()).sort((a, b) => {
    const da = regionMap.get(a)!.district;
    const db = regionMap.get(b)!.district;
    const districtOrder = allDistricts.indexOf(da) - allDistricts.indexOf(db);
    if (districtOrder !== 0) return districtOrder;
    const ra = regionMap.get(a)!.regionName;
    const rb = regionMap.get(b)!.regionName;
    if (ra === nullRegion) return 1;
    if (rb === nullRegion) return -1;
    return ra.localeCompare(rb, "es");
  });

  // Summary tab color = fallback orange (by-region default)
  const summaryTabColor = { argb: TAB_COLORS["by-region"] };

  // Summary sheet — includes Distrito column
  const summary = wb.addWorksheet("Resumen", { properties: { tabColor: summaryTabColor } });
  applyHeaderRow(summary, ["Distrito", "Región", "Registros"], SUMMARY_BG);
  sortedRegionKeys.forEach((key) => {
    const { rows: groupRows, district, regionName } = regionMap.get(key)!;
    summary.addRow([district, regionName, groupRows.length]);
  });
  const totalRow = summary.addRow(["", "TOTAL", opts.rows.length]);
  totalRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_BG } };
  });
  summary.getColumn(1).width = 35;
  summary.getColumn(2).width = 35;
  summary.getColumn(3).width = 12;

  // One sheet per region, colored by parent district.
  // Tab name: "{District} - {Region}" (truncated to 31 chars).
  sortedRegionKeys.forEach((key) => {
    const { rows: groupRows, district, regionName } = regionMap.get(key)!;
    const argb = districtColorMap.get(district) ?? TAB_COLORS["by-region"];
    const tabName = district !== nullDistrict
      ? sanitizeSheetName(`${district} - ${regionName}`)
      : sanitizeSheetName(regionName);
    const sheet = wb.addWorksheet(tabName, {
      properties: { tabColor: { argb } },
    });
    applyHeaderRow(sheet, labels);
    addDataRows(sheet, groupRows, opts.columns);
    autoSizeColumns(sheet, opts.columns, groupRows);
  });

  return wb;
}

// ─── Download ─────────────────────────────────────────────────────────────────

export async function downloadWorkbook(
  wb: ExcelJS.Workbook,
  filename: string
): Promise<void> {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
